import { type ReactNode, useId } from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuRounded from '@mui/icons-material/MenuRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';

export interface SidebarShellProps {
  children: ReactNode;
  brand: ReactNode;
  navigation: ReactNode;
  footer?: ReactNode;
  /** View-specific controls placed beside the navigation opener. */
  overlayActions?: ReactNode;
  /** Hide the desktop sidebar and let content occupy the entire viewport. */
  immersive?: boolean;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  navigationLabel?: string;
  width?: number;
  /** Allow an external guided tour to receive focus while presenting navigation. */
  modalNavigation?: boolean;
}

/** Responsive application frame. Route and account logic belong to the consumer. */
export function SidebarShell({
  children,
  brand,
  navigation,
  footer,
  overlayActions,
  immersive = false,
  open,
  onOpen,
  onClose,
  navigationLabel = 'Main navigation',
  width = 248,
  modalNavigation = true,
}: SidebarShellProps) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const overlay = immersive || !desktop;
  const id = useId();
  const visible = !overlay || open;
  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        bgcolor: 'background.default',
        color: 'text.primary',
      }}
    >
      <Box
        component="a"
        href={`#${id}-main`}
        sx={{
          position: 'fixed',
          top: -100,
          left: 12,
          zIndex: theme.zIndex.modal + 1,
          p: 1.5,
          bgcolor: 'background.paper',
          color: 'text.primary',
          '&:focus': { top: 12 },
        }}
      >
        Skip to content
      </Box>
      {overlay && (
        <Stack
          direction="row"
          spacing={0.5}
          sx={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: theme.zIndex.appBar,
            p: 0.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: '10px',
            bgcolor: 'background.paper',
            boxShadow: '0 2px 8px rgba(0,0,0,0.16)',
          }}
        >
          <Tooltip title="Open navigation">
            <IconButton
              aria-label="Open navigation"
              aria-controls={visible ? `${id}-nav` : undefined}
              aria-expanded={visible}
              onClick={onOpen}
              data-tour-id="nav-toggle"
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                '&:hover': { bgcolor: 'action.selected' },
              }}
            >
              <MenuRounded />
            </IconButton>
          </Tooltip>
          {overlayActions}
        </Stack>
      )}
      <Drawer
        anchor="left"
        variant={
          overlay ? (modalNavigation ? 'temporary' : 'persistent') : 'permanent'
        }
        open={visible}
        onClose={onClose}
        sx={{ width: overlay ? 0 : width, flexShrink: 0 }}
        PaperProps={{
          id: `${id}-nav`,
          component: 'div',
          'aria-label': navigationLabel,
          sx: {
            width,
            maxWidth: 'calc(100vw - 32px)',
            boxSizing: 'border-box',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
            borderRight: 1,
            borderColor: 'divider',
            borderRadius: 0,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, minHeight: 80, gap: 1 }}
        >
          {brand}
          {overlay && (
            <IconButton aria-label="Close navigation" onClick={onClose}>
              <CloseRounded />
            </IconButton>
          )}
        </Stack>
        <Box
          component="nav"
          aria-label={navigationLabel}
          sx={{ px: 1.5, pb: 2, flex: 1 }}
        >
          {navigation}
        </Box>
        {footer && (
          <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
            {footer}
          </Box>
        )}
      </Drawer>
      <Box
        component="main"
        id={`${id}-main`}
        tabIndex={-1}
        sx={{
          flex: 1,
          minWidth: 0,
          position: 'relative',
          pt: overlay && !immersive ? 9 : 0,
          ...(immersive ? { height: '100dvh', overflow: 'hidden' } : {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
export default SidebarShell;
